import React, { useState, useEffect } from 'react';
import {
  CreditCard, Search, CheckCircle, FileText, MoreVertical, Eye, Download
} from 'lucide-react';
import MemberSearchSelect from './MemberSearchSelect';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { useAdminAuth } from '../context/AdminAuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/utils';

const AdminPayments = () => {
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Dialogs
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [statusAction, setStatusAction] = useState(''); // 'Email send' or 'Paid'

  // Forms
  const [confirmForm, setConfirmForm] = useState({ amount_confirmed: 0, notes: '' });
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [requestForm, setRequestForm] = useState({
    member_id: '',
    amount: '',
    currency: 'USD',
    description: '',
    due_date: '',
    payment_method: 'bank_transfer',
    notes: ''
  });
  const [membersList, setMembersList] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);

  const { api, hasPermission } = useAdminAuth();

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (methodFilter) params.append('method', methodFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await api.get(`/api/admin/payments?${params.toString()}`);
      setPayments(res.data.payments || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [search, statusFilter, methodFilter, dateFrom, dateTo]);

  useEffect(() => {
    const fetchMembers = async () => {
      if (!showRequestDialog) return;
      try {
        setMembersLoading(true);
        const res = await api.get('/api/admin/members/?limit=500');
        setMembersList(res.data?.members || []);
      } catch (e) {
        toast.error('Failed to load members');
        setMembersList([]);
      } finally {
        setMembersLoading(false);
      }
    };
    fetchMembers();
  }, [showRequestDialog, api]);

  const handleConfirm = async () => {
    const amountToConfirm = selectedPayment?.amount ?? confirmForm.amount_confirmed;
    if (!amountToConfirm) {
      toast.error('Payment amount is missing');
      return;
    }
    try {
      const res = await api.post(
        `/api/admin/members/payments/${selectedPayment.payment_id}/confirm?amount_confirmed=${amountToConfirm}${confirmForm.notes ? `&notes=${encodeURIComponent(confirmForm.notes)}` : ''}`
      );
      toast.success(res?.data?.message || 'Payment confirmed');
      setShowConfirmDialog(false);
      setConfirmForm({ amount_confirmed: 0, notes: '' });
      fetchPayments();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to confirm payment'));
    }
  };

  const handleRequestPayment = async () => {
    if (!requestForm.member_id || !requestForm.amount || !requestForm.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      await api.post('/api/admin/payments/request', requestForm);
      toast.success('Payment request sent to member');
      setShowRequestDialog(false);
      setRequestForm({
        member_id: '',
        amount: '',
        currency: 'USD',
        description: '',
        due_date: '',
        payment_method: 'bank_transfer',
        notes: ''
      });
      fetchPayments();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to send payment request'));
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedPayment || !statusAction) return;
    const isBankTransferPaid = selectedPayment.gateway === 'Bank Transfer' && statusAction === 'Paid';
    if (isBankTransferPaid && !invoiceNumber?.trim()) {
      toast.error('Please enter invoice number to mark as Paid');
      return;
    }
    try {
      const payload = { status: statusAction };
      if (isBankTransferPaid) payload.invoice_number = invoiceNumber.trim();
      await api.patch(`/api/admin/payments/${selectedPayment.payment_id}/status`, payload);
      toast.success(`Status updated to ${statusAction}`);
      setShowStatusDialog(false);
      setSelectedPayment(null);
      setStatusAction('');
      setInvoiceNumber('');
      fetchPayments();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to update status'));
    }
  };

  const getStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
      requested: { bg: 'bg-amber-100', text: 'text-amber-700' },
      'email send': { bg: 'bg-blue-100', text: 'text-blue-700' },
      paid: { bg: 'bg-green-100', text: 'text-green-700' },
      completed: { bg: 'bg-green-100', text: 'text-green-700' },
      confirmed: { bg: 'bg-green-100', text: 'text-green-700' },
      proof_uploaded: { bg: 'bg-blue-100', text: 'text-blue-700' },
      rejected: { bg: 'bg-red-100', text: 'text-red-700' }
    };
    return badges[s] || badges.pending;
  };

  const getMethodIcon = (method) => {
    const m = (method || '').toLowerCase();
    const icons = {
      stripe: '💳',
      paypal: '🅿️',
      razorpay: '🇮🇳',
      'bank transfer': '🏦',
      bank_transfer: '🏦'
    };
    return icons[m] || '💰';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payments Management</h1>
          <p className="text-gray-500">View and confirm payment proofs</p>
        </div>
        <Button
          onClick={() => setShowRequestDialog(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <CreditCard size={16} className="mr-2" />
          Request Payment
        </Button>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">Search</Label>
              <div className="relative mt-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Payment ID, Member ID, Company, Email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchPayments()}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="w-40">
              <Label className="text-xs">Status</Label>
              <select
                className="w-full px-3 py-2 border rounded-md mt-1"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value="Pending">Pending</option>
                <option value="Requested">Requested</option>
                <option value="Email send">Email send</option>
                <option value="Paid">Paid</option>
              </select>
            </div>
            <div className="w-40">
              <Label className="text-xs">Method</Label>
              <select
                className="w-full px-3 py-2 border rounded-md mt-1"
                value={methodFilter}
                onChange={(e) => setMethodFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="razorpay">Razorpay</option>
              </select>
            </div>
            <div className="w-36">
              <Label className="text-xs">Date From</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1" />
            </div>
            <div className="w-36">
              <Label className="text-xs">Date To</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Records Table */}
      <Card className="overflow-hidden">
        <CardContent className="p-0 overflow-hidden">
          <div className="overflow-x-hidden">
            <table className="w-full table-fixed text-xs">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase w-[90px]">Member ID</th>
                  <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase w-[120px]">Company</th>
                  <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase w-[140px]">Email</th>
                  <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase w-[70px]">Tier</th>
                  <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase w-[90px]">Method</th>
                  <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase w-[80px]">Amount</th>
                  <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase w-[80px]">Status</th>
                  <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase w-[75px]">Date</th>
                  <th className="px-2 py-2 text-right text-[10px] font-medium text-gray-500 uppercase whitespace-nowrap w-[65px]">Actions</th>
                </tr>
              </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-2 py-6 text-center text-gray-500">Loading...</td>
                </tr>
              ) : payments.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-2 py-6 text-center text-gray-500">
                    No payments found
                  </td>
                </tr>
              ) : (
                payments.map((payment) => {
                  const statusBadge = getStatusBadge(payment.status);
                  const status = (payment.status || '').toLowerCase();
                  const hasBankTransferRequested = payment.gateway === 'Bank Transfer' && status === 'requested';
                  const hasBankTransferEmailSend = payment.gateway === 'Bank Transfer' && status === 'email send';
                  const isEventPayment = payment.reference_type === 'event_registration';
                  const confirmableStatuses = isEventPayment
                    ? ['pending', 'proof_uploaded', 'processing']
                    : ['pending', 'proof_uploaded', 'completed', 'processing'];
                  const hasConfirm = hasPermission('payments', 'confirm') && confirmableStatuses.includes(status);
                  const hasAnyAction = hasBankTransferRequested || hasBankTransferEmailSend || hasConfirm;
                  return (
                    <tr
                      key={payment.payment_id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={(e) => {
                        if (e.target.closest('button') || e.target.closest('[role="menu"]')) return;
                        setSelectedPayment(payment);
                        setShowDetailsDialog(true);
                      }}
                    >
                      <td className="px-2 py-2 font-mono truncate" title={payment.member_id}>{payment.member_id}</td>
                      <td className="px-2 py-2 truncate" title={payment.company_name}>{payment.company_name || '—'}</td>
                      <td className="px-2 py-2 truncate" title={payment.email}>{payment.email || '—'}</td>
                      <td className="px-2 py-2 truncate">{payment.tier || '—'}</td>
                      <td className="px-2 py-2">
                        <span className="mr-1">{getMethodIcon(payment.payment_method)}</span>
                        <span className="truncate">{payment.payment_method || 'Pending'}</span>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        <span className="font-medium">${payment.amount?.toLocaleString()}</span>
                        <span className="text-gray-500 ml-0.5">{payment.currency || 'USD'}</span>
                      </td>
                      <td className="px-2 py-2">
                        <Badge className={`${statusBadge.bg} ${statusBadge.text} text-[10px]`}>
                          {payment.status || 'Pending'}
                        </Badge>
                      </td>
                      <td className="px-2 py-2 text-gray-500 whitespace-nowrap">
                        {payment.created_at ? new Date(payment.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-2 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                        {hasAnyAction ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                <MoreVertical size={14} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {hasBankTransferRequested && (
                                <DropdownMenuItem
                                  onClick={() => {
                                    setSelectedPayment(payment);
                                    setStatusAction('Email send');
                                    setShowStatusDialog(true);
                                  }}
                                >
                                  Mark as Email send
                                </DropdownMenuItem>
                              )}
                              {hasBankTransferEmailSend && (
                                <DropdownMenuItem
                                  onClick={() => {
                                    setSelectedPayment(payment);
                                    setStatusAction('Paid');
                                    setShowStatusDialog(true);
                                  }}
                                >
                                  <CheckCircle size={14} className="mr-2" />
                                  Mark as Paid
                                </DropdownMenuItem>
                              )}
                              {hasConfirm && (
                                <DropdownMenuItem
                                  onClick={() => {
                                    setSelectedPayment(payment);
                                    setConfirmForm({ amount_confirmed: payment.amount, notes: '' });
                                    setShowConfirmDialog(true);
                                  }}
                                >
                                  <CheckCircle size={14} className="mr-2" />
                                  Confirm
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          </div>
          <div className="px-4 py-2 border-t text-sm text-gray-500">
            Total: {total} payment(s)
          </div>
        </CardContent>
      </Card>

      {/* Status Update Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={(open) => { setShowStatusDialog(open); if (!open) setInvoiceNumber(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Payment Status</DialogTitle>
          </DialogHeader>
          {selectedPayment && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Payment ID:</span>
                  <span className="font-mono">{selectedPayment.payment_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Member:</span>
                  <span>{selectedPayment.member_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Amount:</span>
                  <span className="font-bold">${selectedPayment.amount?.toLocaleString()}</span>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                {statusAction === 'Email send' && 'Confirm that you have sent bank account details to the member.'}
                {statusAction === 'Paid' && 'Confirm that payment has been received. This will activate the member\'s membership.'}
              </p>
              {selectedPayment.gateway === 'Bank Transfer' && statusAction === 'Paid' && (
                <div>
                  <Label>Invoice Number *</Label>
                  <Input
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder="Enter invoice number"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Invoice number is required to mark Bank Transfer payments as Paid.
                  </p>
                </div>
              )}
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => { setShowStatusDialog(false); setInvoiceNumber(''); }}>Cancel</Button>
                <Button
                  onClick={handleStatusUpdate}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={selectedPayment.gateway === 'Bank Transfer' && statusAction === 'Paid' && !invoiceNumber?.trim()}
                >
                  Update to {statusAction}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Details Dialog */}
      {selectedPayment && (
        <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <CreditCard size={20} className="mr-2 text-blue-600" />
                Payment Details - {selectedPayment.payment_id}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 mb-1">Payment ID</p>
                    <p className="font-mono font-medium text-gray-900">{selectedPayment.payment_id}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Member ID</p>
                    <p className="font-medium text-gray-900">{selectedPayment.member_id}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Company Name</p>
                    <p className="font-medium text-gray-900">{selectedPayment.company_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Individual Name</p>
                    <p className="font-medium text-gray-900">{selectedPayment.individual_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Email</p>
                    <p className="font-medium text-gray-900">{selectedPayment.email || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Tier</p>
                    <p className="font-medium text-gray-900">{selectedPayment.tier || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Payment Method</p>
                    <p className="font-medium text-gray-900">{selectedPayment.payment_method || selectedPayment.gateway || 'Pending'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Status</p>
                    <Badge className={`${getStatusBadge(selectedPayment.status).bg} ${getStatusBadge(selectedPayment.status).text}`}>
                      {selectedPayment.status || 'Pending'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Amount</p>
                    <p className="font-medium text-gray-900">
                      {selectedPayment.currency === 'INR'
                        ? `₹${selectedPayment.amount_inr?.toLocaleString() || selectedPayment.amount?.toLocaleString() || '0'}`
                        : `$${selectedPayment.amount?.toLocaleString() || '0'}`}
                      {selectedPayment.currency && (
                        <span className="text-gray-500 ml-1">({selectedPayment.currency})</span>
                      )}
                    </p>
                  </div>
                  {selectedPayment.amount_inr != null && selectedPayment.currency !== 'INR' && (
                    <div>
                      <p className="text-gray-500 mb-1">Amount (INR)</p>
                      <p className="font-medium text-gray-900">₹{selectedPayment.amount_inr?.toLocaleString() || '-'}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-gray-500 mb-1">Invoice Number</p>
                    <p className="font-medium text-gray-900">{selectedPayment.invoice_number || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Product</p>
                    <p className="font-medium text-gray-900">{selectedPayment.product_id || selectedPayment.tier || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Payment Type</p>
                    <p className="font-medium text-gray-900">{selectedPayment.payment_type || selectedPayment.reference_type || '-'}</p>
                  </div>
                  {selectedPayment.reference_type === 'event_registration' && (
                    <>
                      <div>
                        <p className="text-gray-500 mb-1">Event Name</p>
                        <p className="font-medium text-gray-900">{selectedPayment.event_name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-1">Registration ID</p>
                        <p className="font-mono font-medium text-gray-900">{selectedPayment.registration_id || '-'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-1">Pass Count</p>
                        <p className="font-medium text-gray-900">{selectedPayment.total_passes || 1}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-1">Event Date</p>
                        <p className="font-medium text-gray-900">
                          {selectedPayment.event_start_date
                            ? new Date(selectedPayment.event_start_date).toLocaleString()
                            : '-'}
                        </p>
                      </div>
                    </>
                  )}
                  <div>
                    <p className="text-gray-500 mb-1">Created At</p>
                    <p className="font-medium text-gray-900">
                      {selectedPayment.created_at
                        ? new Date(selectedPayment.created_at).toLocaleString()
                        : '-'}
                    </p>
                  </div>
                  {selectedPayment.status_updated_at && (
                    <div>
                      <p className="text-gray-500 mb-1">Status Updated At</p>
                      <p className="font-medium text-gray-900">
                        {new Date(selectedPayment.status_updated_at).toLocaleString()}
                      </p>
                    </div>
                  )}
                  {selectedPayment.bank_transfer_proof && (
                    <div className="col-span-2">
                      <p className="text-gray-500 mb-1">Attached proof</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-1.5"
                          onClick={() => window.open(selectedPayment.bank_transfer_proof, '_blank', 'noopener,noreferrer')}
                        >
                          <Eye size={14} />
                          View
                        </Button>
                        <a
                          href={selectedPayment.bank_transfer_proof}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50"
                        >
                          <Download size={14} />
                          Download
                        </a>
                      </div>
                    </div>
                  )}
                  {selectedPayment.payment_link_url && (
                    <div className="col-span-2">
                      <p className="text-gray-500 mb-1">Payment Link</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-1.5"
                          onClick={() => window.open(selectedPayment.payment_link_url, '_blank', 'noopener,noreferrer')}
                        >
                          <Eye size={14} />
                          Open Payment Link
                        </Button>
                        <a
                          href={selectedPayment.payment_link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 break-all"
                        >
                          {selectedPayment.payment_link_url}
                        </a>
                      </div>
                    </div>
                  )}
                  {(selectedPayment.transaction_reference || selectedPayment.transfer_date) && (
                    <>
                      <div>
                        <p className="text-gray-500 mb-1">Transaction Reference</p>
                        <p className="font-medium text-gray-900">{selectedPayment.transaction_reference || '-'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-1">Transfer Date</p>
                        <p className="font-medium text-gray-900">{selectedPayment.transfer_date || '-'}</p>
                      </div>
                    </>
                  )}
                  {(selectedPayment.payer_account_name || selectedPayment.payer_bank_name || selectedPayment.payer_account_last4) && (
                    <>
                      <div>
                        <p className="text-gray-500 mb-1">Payer Account Name</p>
                        <p className="font-medium text-gray-900">{selectedPayment.payer_account_name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-1">Payer Bank</p>
                        <p className="font-medium text-gray-900">
                          {selectedPayment.payer_bank_name || '-'}
                          {selectedPayment.payer_account_last4 ? ` • ****${selectedPayment.payer_account_last4}` : ''}
                        </p>
                      </div>
                    </>
                  )}
                  {selectedPayment.gst_details && Object.keys(selectedPayment.gst_details).length > 0 && (
                    <>
                      <div>
                        <p className="text-gray-500 mb-1">GST Number</p>
                        <p className="font-medium text-gray-900">{selectedPayment.gst_details.gst_number || '-'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-1">Company Legal Name</p>
                        <p className="font-medium text-gray-900">{selectedPayment.gst_details.company_legal_name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-1">Billing User</p>
                        <p className="font-medium text-gray-900">{selectedPayment.gst_details.user_name || '-'}</p>
                      </div>
                      {selectedPayment.razorpay_payment_link_id && (
                        <div>
                          <p className="text-gray-500 mb-1">Razorpay Link ID</p>
                          <p className="font-mono font-medium text-gray-900">{selectedPayment.razorpay_payment_link_id}</p>
                        </div>
                      )}
                    </>
                  )}
                  {Array.isArray(selectedPayment.selected_fees) && selectedPayment.selected_fees.length > 0 && (
                    <div className="col-span-2">
                      <p className="text-gray-500 mb-2">Fee Breakdown</p>
                      <div className="space-y-2 rounded-md border bg-gray-50 p-3">
                        {selectedPayment.selected_fees.map((fee, index) => (
                          <div key={`${fee.fee_id || fee.name}-${index}`} className="flex items-center justify-between text-sm">
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900">{fee.name || 'Fee'}</p>
                              <p className="text-gray-500">Qty {fee.quantity || 0}</p>
                            </div>
                            <p className="font-medium text-gray-900">
                              {selectedPayment.currency === 'INR'
                                ? `₹${Number(fee.line_total || 0).toLocaleString()}`
                                : `$${Number(fee.line_total || 0).toLocaleString()}`}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {(selectedPayment.confirmation_notes != null && selectedPayment.confirmation_notes !== '') && (
                    <div className="col-span-2">
                      <p className="text-gray-500 mb-1">Confirmation notes</p>
                      <p className="font-medium text-gray-900 whitespace-pre-wrap">{selectedPayment.confirmation_notes}</p>
                    </div>
                  )}
                  {selectedPayment.amount_confirmed != null && (
                    <div>
                      <p className="text-gray-500 mb-1">Confirmed amount</p>
                      <p className="font-medium text-gray-900">
                        {selectedPayment.currency === 'INR'
                          ? `₹${Number(selectedPayment.amount_confirmed).toLocaleString()}`
                          : `$${Number(selectedPayment.amount_confirmed).toLocaleString()}`}
                      </p>
                    </div>
                  )}
                  {selectedPayment.confirmed_at && (
                    <div>
                      <p className="text-gray-500 mb-1">Confirmed at</p>
                      <p className="font-medium text-gray-900">
                        {new Date(selectedPayment.confirmed_at).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Confirm Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-h-[90vh] flex flex-col overflow-hidden p-6 gap-0">
          <DialogHeader className="flex-shrink-0 pb-2 border-b border-gray-200 pr-10">
            <DialogTitle className="flex items-center">
              <CheckCircle size={20} className="mr-2 text-green-600" />
              Confirm Payment
            </DialogTitle>
          </DialogHeader>
          {selectedPayment && (
            <div className="flex-1 min-h-0 overflow-y-auto px-4 pt-4 space-y-4 [scrollbar-gutter:stable]">
              <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Payment ID:</span>
                  <span className="font-mono">{selectedPayment.payment_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Member:</span>
                  <span>{selectedPayment.member_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Original Amount:</span>
                  <span className="font-bold">${selectedPayment.amount?.toLocaleString()}</span>
                </div>
              </div>

              {selectedPayment.gateway === 'Bank Transfer' && selectedPayment.bank_transfer_proof && (
                <div className="p-4 border rounded-lg bg-gray-50">
                  <Label className="text-sm font-medium">Attached proof</Label>
                  <p className="text-xs text-gray-500 mt-0.5 mb-2">Bank transfer receipt / screenshot</p>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => window.open(selectedPayment.bank_transfer_proof, '_blank', 'noopener,noreferrer')}
                    >
                      <Eye size={14} />
                      View
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      asChild
                    >
                      <a href={selectedPayment.bank_transfer_proof} download target="_blank" rel="noopener noreferrer">
                        <Download size={14} />
                        Download
                      </a>
                    </Button>
                  </div>
                </div>
              )}

              <div>
                <Label>Confirmed Amount</Label>
                <p className="mt-1 font-semibold text-gray-900">
                  {selectedPayment.currency === 'INR'
                    ? `₹${(selectedPayment.amount ?? 0).toLocaleString()}`
                    : `$${(selectedPayment.amount ?? 0).toLocaleString()}`}
                  {selectedPayment.currency && (
                    <span className="text-gray-500 font-normal ml-1">({selectedPayment.currency})</span>
                  )}
                </p>
              </div>

              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={confirmForm.notes}
                  onChange={(e) => setConfirmForm({ ...confirmForm, notes: e.target.value })}
                  placeholder="Any notes about the payment..."
                  rows={2}
                  className="mt-1"
                />
              </div>

              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <FileText size={16} className="inline mr-2" />
                An invoice will be automatically generated upon confirmation.
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleConfirm} className="bg-green-600 hover:bg-green-700 text-white">
                  <CheckCircle size={16} className="mr-2" />
                  Confirm Payment
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Request Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={(open) => {
        setShowRequestDialog(open);
        if (!open) {
          setRequestForm({
            member_id: '',
            amount: '',
            currency: 'USD',
            description: '',
            due_date: '',
            payment_method: 'bank_transfer',
            notes: ''
          });
        }
      }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto p-6 w-full [scrollbar-gutter:stable]">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <CreditCard size={20} className="mr-2 text-blue-600" />
              Request Payment from Member
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="min-w-0">
              <MemberSearchSelect
                members={membersList}
                value={requestForm.member_id}
                onChange={(member_id) => setRequestForm((f) => ({ ...f, member_id }))}
                disabled={membersLoading}
              />
              {membersLoading && (
                <p className="text-xs text-gray-500 mt-1">Loading members...</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 min-w-0">
              <div className="min-w-0">
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={requestForm.amount}
                  onChange={(e) => setRequestForm({ ...requestForm, amount: e.target.value })}
                  placeholder="0.00"
                  className="mt-1 w-full max-w-full"
                />
              </div>
              <div className="min-w-0">
                <Label>Currency</Label>
                <select
                  className="w-full max-w-full px-3 py-2 border rounded-md mt-1"
                  value={requestForm.currency}
                  onChange={(e) => setRequestForm({ ...requestForm, currency: e.target.value })}
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="INR">INR</option>
                  <option value="SGD">SGD</option>
                </select>
              </div>
            </div>

            <div className="min-w-0">
              <Label>Due Date</Label>
              <Input
                type="date"
                value={requestForm.due_date}
                onChange={(e) => setRequestForm({ ...requestForm, due_date: e.target.value })}
                className="mt-1 w-full max-w-full"
              />
            </div>

            <div className="min-w-0">
              <Label>Payment Method</Label>
              <select
                className="w-full max-w-full px-3 py-2 border rounded-md mt-1"
                value={requestForm.payment_method}
                onChange={(e) => setRequestForm({ ...requestForm, payment_method: e.target.value })}
              >
                <option value="bank_transfer">Bank Transfer</option>
                <option value="stripe">Credit Card (Stripe)</option>
                <option value="paypal">PayPal</option>
                <option value="razorpay">Razorpay</option>
              </select>
            </div>

            <div className="min-w-0">
              <Label>Description *</Label>
              <Textarea
                value={requestForm.description}
                onChange={(e) => setRequestForm({ ...requestForm, description: e.target.value })}
                placeholder="e.g., Annual membership fee for Gold tier"
                rows={2}
                className="mt-1 w-full max-w-full"
              />
            </div>

            <div className="min-w-0">
              <Label>Additional Notes (Optional)</Label>
              <Textarea
                value={requestForm.notes}
                onChange={(e) => setRequestForm({ ...requestForm, notes: e.target.value })}
                placeholder="Any additional information for the member..."
                rows={2}
                className="mt-1 w-full max-w-full"
              />
            </div>

            {/* <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <FileText size={16} className="inline mr-2" />
                An invoice will be generated and an email will be sent to the member.
              </div> */}

            <div className="flex justify-end space-x-2 pt-4 border-t mt-4">
              <Button variant="outline" onClick={() => setShowRequestDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleRequestPayment} className="bg-blue-600 hover:bg-blue-700 text-white">
                <CreditCard size={16} className="mr-2" />
                Send Payment Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPayments;
